// 고객 A/S 접수(`0131` as_request) — 행 모양·상태 옮김·사진 키는 저장소 밖 한 벌(인메모리·Supabase 공통).

import { z } from "zod";

import { CheckViolationError } from "../../lib/check-violation";
import { isUuid } from "../../lib/uuid";

export const AS_REQUEST_STATUSES = ["OPEN", "SCHEDULED", "DONE"] as const;
export type AsRequestStatus = (typeof AS_REQUEST_STATUSES)[number];

export const AS_REQUEST_STATUS_LABEL: Record<AsRequestStatus, string> = { OPEN: "접수", SCHEDULED: "방문 예정", DONE: "처리 완료" };

export const AS_REQUEST_CATEGORIES = ["욕실", "주방", "거실", "방", "창호·문", "바닥·벽", "전기·조명", "기타"] as const;
export type AsRequestCategory = (typeof AS_REQUEST_CATEGORIES)[number];

export const MAX_AS_REQUEST_BODY = 2000;
export const MAX_AS_REQUEST_PHOTOS = 6;

export interface AsRequest {
  id: string;
  org_id: string;
  project_id: string;
  // 분류를 제목으로 쓴다(0131 title 1~80자).
  title: string;
  body: string;
  status: AsRequestStatus;
  photo_keys: string[];
  scheduled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AsRequestStatusPatch {
  // 화면이 본 상태 — 그사이 누가 옮겼으면 AsRequestStaleError.
  from: AsRequestStatus;
  status: AsRequestStatus;
  scheduled_at: string | null;
}

export const AS_REQUEST_CATEGORY_MESSAGE = "분류를 골라 주세요";
export const AS_REQUEST_EMPTY_MESSAGE = "증상을 적어 주세요";
export const AS_REQUEST_TOO_LONG_MESSAGE = `내용은 ${MAX_AS_REQUEST_BODY}자까지 쓸 수 있습니다`;
export const AS_REQUEST_TOO_MANY_PHOTOS_MESSAGE = `사진은 ${MAX_AS_REQUEST_PHOTOS}장까지 올릴 수 있습니다`;
export const AS_REQUEST_TARGET_MESSAGE = "A/S 를 접수할 수 없는 현장입니다";
export const AS_REQUEST_RATE_MESSAGE = "접수가 너무 잦습니다. 잠시 뒤 다시 보내 주세요";
export const AS_REQUEST_TRANSITION_MESSAGE = "지금 상태에서는 그렇게 바꿀 수 없습니다";
export const AS_REQUEST_SCHEDULE_MESSAGE = "방문 일시를 정해 주세요";
export const AS_REQUEST_STALE_MESSAGE = "그사이 다른 사람이 상태를 바꿨습니다. 새로고침해 주세요";
export const AS_REQUEST_ID_MESSAGE = "접수 번호가 올바르지 않습니다. 화면을 새로고침해 주세요";

// 화면이 폼마다 만든 접수 id — 재시도를 같은 접수로 묶는다 · 소문자로 맞춘다(PG uuid 표기와 같게).
export const asRequestIdSchema = z
  .string({ message: AS_REQUEST_ID_MESSAGE })
  .trim()
  .refine(isUuid, { message: AS_REQUEST_ID_MESSAGE })
  .transform((value) => value.toLowerCase());

// PG 의 char_length 는 코드포인트를 센다(0131 as_request_body_len 과 같은 셈).
const pgLength = (value: string): number => Array.from(value).length;

export const asRequestCategorySchema = z.enum(AS_REQUEST_CATEGORIES, { message: AS_REQUEST_CATEGORY_MESSAGE });

export const asRequestBodySchema = z
  .string({ message: AS_REQUEST_EMPTY_MESSAGE })
  .transform((value) => value.trim())
  .refine((value) => value.length > 0, { message: AS_REQUEST_EMPTY_MESSAGE })
  .refine((value) => pgLength(value) <= MAX_AS_REQUEST_BODY, { message: AS_REQUEST_TOO_LONG_MESSAGE });

// 옮길 수 있는 길 — 끝(DONE)은 되돌리지 않는다 · 예정→예정은 일시 바꾸기 · 예정→접수는 일정 취소.
const NEXT: Record<AsRequestStatus, readonly AsRequestStatus[]> = {
  OPEN: ["SCHEDULED", "DONE"],
  SCHEDULED: ["SCHEDULED", "OPEN", "DONE"],
  DONE: [],
};

export const canMoveAsRequest = (from: AsRequestStatus, to: AsRequestStatus): boolean => NEXT[from].includes(to);

export class AsRequestTargetError extends Error {
  constructor() {
    super(AS_REQUEST_TARGET_MESSAGE);
    this.name = "AsRequestTargetError";
  }
}

// 같은 id 의 접수가 이미 있다(pkey) — 재시도인지는 부르는 쪽이 토큰 범위로 다시 읽어 가른다.
export class AsRequestDuplicateError extends Error {
  constructor() {
    super(AS_REQUEST_ID_MESSAGE);
    this.name = "AsRequestDuplicateError";
  }
}

export class AsRequestBlockedError extends Error {
  constructor() {
    super(AS_REQUEST_RATE_MESSAGE);
    this.name = "AsRequestBlockedError";
  }
}

export class AsRequestTransitionError extends Error {
  constructor(message = AS_REQUEST_TRANSITION_MESSAGE) {
    super(message);
    this.name = "AsRequestTransitionError";
  }
}

export class AsRequestStaleError extends Error {
  constructor() {
    super(AS_REQUEST_STALE_MESSAGE);
    this.name = "AsRequestStaleError";
  }
}

// 다음 상태의 쓰기 값 — 예정만 일시를 남기고 나머지는 비운다.
export const buildAsRequestStatusPatch = (from: AsRequestStatus, to: AsRequestStatus, scheduledAt: string | null): AsRequestStatusPatch => {
  if (!canMoveAsRequest(from, to)) throw new AsRequestTransitionError();
  if (to === "SCHEDULED" && !scheduledAt) throw new AsRequestTransitionError(AS_REQUEST_SCHEDULE_MESSAGE);
  return { from, status: to, scheduled_at: to === "SCHEDULED" ? scheduledAt : null };
};

// 저장소가 쓰기 전에 대조한다 — 화면이 본 상태와 지금 상태가 달라야 stale.
export const applyAsRequestStatus = (row: AsRequest, patch: AsRequestStatusPatch, at: string): AsRequest => {
  if (row.status !== patch.from) throw new AsRequestStaleError();
  if (!canMoveAsRequest(row.status, patch.status)) throw new AsRequestTransitionError();
  return { ...row, status: patch.status, scheduled_at: patch.scheduled_at, updated_at: at };
};

export const asRequestPhotoPrefix = (orgId: string, projectId: string, asId: string): string =>
  `org/${orgId}/project/${projectId}/as/${asId}/`;

export const asRequestPhotoKey = (orgId: string, projectId: string, asId: string, index: number, ext: string): string =>
  `${asRequestPhotoPrefix(orgId, projectId, asId)}${index}.${ext}`;

export const buildAsRequestRow = (input: {
  id: string;
  org_id: string;
  project_id: string;
  category: AsRequestCategory;
  body: string;
  photo_keys: string[];
  at: string;
}): AsRequest => ({
  id: input.id,
  org_id: input.org_id,
  project_id: input.project_id,
  title: input.category,
  body: input.body,
  status: "OPEN",
  photo_keys: [...input.photo_keys],
  scheduled_at: null,
  created_at: input.at,
  updated_at: input.at,
});

export const MAX_AS_REQUEST_PHOTO_KEY = 300;

const violation = (constraint: string): CheckViolationError =>
  new CheckViolationError(constraint, `new row for relation "as_request" violates check constraint "${constraint}"`);

// 행 검사 — 0131 check·트리거와 같은 판정(인메모리가 쓴다) · 현장이 같은 조직인지는 저장소가 표로 본다.
export const assertAsRequestRow = (row: AsRequest): void => {
  if (pgLength(row.title.trim()) < 1 || pgLength(row.title.trim()) > 80) throw violation("as_request_title_len");
  if (pgLength(row.body.trim()) < 1 || pgLength(row.body.trim()) > MAX_AS_REQUEST_BODY) throw violation("as_request_body_len");
  if (row.photo_keys.length > MAX_AS_REQUEST_PHOTOS) throw violation("as_request_photo_max");
  const prefix = `org/${row.org_id}/project/${row.project_id}/`;
  const badKey = (k: string | null): boolean => typeof k !== "string" || pgLength(k) > MAX_AS_REQUEST_PHOTO_KEY || !k.startsWith(prefix);
  if (row.photo_keys.some(badKey)) throw violation("as_request_guard");
};

// 0131 as_request_guard 의 현장 조건 — 그 조직의 현장이어야 한다.
export const assertAsRequestProject = (row: Pick<AsRequest, "org_id" | "project_id">, project: { org_id: string } | null | undefined): void => {
  if (!project || project.org_id !== row.org_id) throw violation("as_request_guard");
};

// 최근 접수가 위.
export const compareAsRequests = (a: AsRequest, b: AsRequest): number =>
  a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : a.id < b.id ? 1 : a.id > b.id ? -1 : 0;

// 고객에게 보이는 모양 — 저장 키는 숨기고 장 수만.
export interface CustomerAsRequest {
  id: string;
  category: string;
  body: string;
  status: AsRequestStatus;
  status_label: string;
  scheduled_at: string | null;
  photo_count: number;
  created_at: string;
}

export const customerAsRequestOf = (row: AsRequest): CustomerAsRequest => ({
  id: row.id,
  category: row.title,
  body: row.body,
  status: row.status,
  status_label: AS_REQUEST_STATUS_LABEL[row.status],
  scheduled_at: row.scheduled_at,
  photo_count: row.photo_keys.length,
  created_at: row.created_at,
});
